import * as React from 'react';
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { useLayer } from '@coursera-web/statsig';

import useUserAgent from 'bundles/page/hooks/useUserAgent';
import { CoachStateProvider, useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';
import { TwoPanelChatRouteProvider } from 'bundles/unified-common/providers/TwoPanelChatRouteProvider';
import { useIsCourseDataLoading } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import { useIsS12nDataLoading } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import useIsDesktop from 'bundles/unified-description-page-common/hooks/useIsDesktop';

const LAYER_NAME = 'consumer-xdp-two-panel-test-3-layer';
const PARAM_NAME = 'chat-panel-location';
const CHAT_ON_LEFT = 'left';
const CHAT_ON_RIGHT = 'right';
const NO_CHAT_PANEL = 'none';

export type TwoPanelConversationalDiscoveryExperimentContextType = {
  twoPanelConversationalDiscoveryEnabled: boolean;
  chatOnLeft: boolean;
  chatOnRight: boolean;
};

export const TwoPanelConversationalDiscoveryExperimentContext =
  createContext<TwoPanelConversationalDiscoveryExperimentContextType>({
    twoPanelConversationalDiscoveryEnabled: false,
    chatOnLeft: false,
    chatOnRight: false,
  });

// useLayoutEffect warns during SSR; useEffect is inert there and keeps the server render silent.
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Opens the chat during CSR once the viewport measures ≥1024px, and closes it below.
 * coachOpen starts false and only ever controls the chat *content* — the panel container
 * (aside + grid track) is governed by useTwoPanelPanelVisibility below, so the chat/session
 * never starts on viewports where the panel is hidden, and the career-fit surfaces (which
 * share this coachOpen) start closed below 1024px instead of auto-opening.
 */
const CoachOpenViewportSync = () => {
  const isDesktop = useIsDesktop();
  const { setCoachOpen } = useCoachState();

  // Layout effect (not passive): the viewport measurement that closes the chat below 1024px is
  // the same one that flips the career-fit surfaces on — a paint in between would flash the
  // legacy chat open with the inherited coachOpen=true before this sync closes it.
  useIsomorphicLayoutEffect(() => {
    setCoachOpen(isDesktop);
  }, [isDesktop, setCoachOpen]);

  return null;
};

export const TwoPanelPanelVisibilityContext = createContext(false);

/**
 * Whether the docked chat panel container (the aside and its reserved grid track) should be
 * rendered. True on the server and through hydration — the panel container and its skeleton are
 * part of the SSR HTML, so desktop-width first paints are already two-panel even though
 * coachOpen starts false. After the first client viewport measurement it follows coachOpen
 * (opened ≥1024px by CoachOpenViewportSync) gated on the desktop check, so a career-fit chat
 * opening below 1024px never resurrects the hidden panel container.
 */
const PanelVisibilityProvider = ({ children }: React.PropsWithChildren) => {
  const { coachOpen } = useCoachState();
  const isDesktop = useIsDesktop();
  const [viewportMeasured, setViewportMeasured] = useState(false);

  // Layout effect so it lands in the same pre-paint flush as CoachOpenViewportSync — the panel
  // container never paints in a state that disagrees with the measured viewport.
  useIsomorphicLayoutEffect(() => {
    setViewportMeasured(true);
  }, []);

  const panelVisible = (coachOpen && isDesktop) || !viewportMeasured;

  return (
    <TwoPanelPanelVisibilityContext.Provider value={panelVisible}>{children}</TwoPanelPanelVisibilityContext.Provider>
  );
};

export const useTwoPanelPanelVisibility = () => useContext(TwoPanelPanelVisibilityContext);

function TwoPanelConversationalDiscoveryExperimentProvider({ children }: React.PropsWithChildren) {
  const isS12nDataLoading = useIsS12nDataLoading();
  const isCourseDataLoading = useIsCourseDataLoading();

  // Eligibility is UA-only, mirroring the Statsig gate's mobile-browser filter. The UA is fixed
  // for the session, so `location` can never flip between SSR/hydration and later client renders —
  // a flip would reparent (unmount/remount) the whole page subtree below. Sub-1024px desktop
  // viewports keep the treatment tree; the panel is handled by breakpoint-scoped CSS plus
  // useTwoPanelPanelVisibility/CoachOpenViewportSync below.
  const userAgent = useUserAgent();
  const isMobileOrTabletUserAgent = userAgent?.isMobileBrowser || userAgent?.isMobile;

  const layer = useLayer(LAYER_NAME);
  const rawLocation = layer.get(PARAM_NAME, NO_CHAT_PANEL);
  const location = isMobileOrTabletUserAgent ? NO_CHAT_PANEL : rawLocation;

  const value = useMemo(() => {
    const chatOnLeft = location === CHAT_ON_LEFT;
    const chatOnRight = location === CHAT_ON_RIGHT;
    return {
      twoPanelConversationalDiscoveryEnabled: chatOnLeft || chatOnRight,
      chatOnLeft,
      chatOnRight,
    };
  }, [location]);

  return (
    <TwoPanelConversationalDiscoveryExperimentContext.Provider value={value}>
      {value.twoPanelConversationalDiscoveryEnabled ? (
        <CoachStateProvider coachOpen={false} isContextConfigLoading={isS12nDataLoading || isCourseDataLoading}>
          <CoachOpenViewportSync />
          <PanelVisibilityProvider>
            <TwoPanelChatRouteProvider>{children}</TwoPanelChatRouteProvider>
          </PanelVisibilityProvider>
        </CoachStateProvider>
      ) : (
        children
      )}
    </TwoPanelConversationalDiscoveryExperimentContext.Provider>
  );
}

export const useTwoPanelConversationalDiscoveryExperiment = () => {
  return useContext(TwoPanelConversationalDiscoveryExperimentContext);
};

export default TwoPanelConversationalDiscoveryExperimentProvider;
